import { useEffect, useRef, useState } from 'react';
import { getFullAPIFileURL } from '../../common/getFullAPIFileURL';
import type { CoverAiPicture } from '../../../redux/api/fymAPI.types';
import type { CoverEditorDispatch } from '../doc/useCoverEditorDoc';
import type { FirstVersionNote } from './EditorNotices';

interface UseFirstPictureSwapProps {
  /** The AI run that the first open started; null when it started none. */
  runUuid: string | null;
  /** What the first open made, or null when this is not a first open. */
  initialNote: FirstVersionNote;
  pictures: readonly CoverAiPicture[];
  /** The count of the changes of the document: 0 while the user did nothing. */
  revision: number;
  dispatch: CoverEditorDispatch;
}

/**
 * The first AI picture of a first open (owner decision D4): the editor opens on a bank picture at
 * once; when the first picture of the run is ready and the user changed nothing, it becomes the
 * background by itself. After a change of the user it only waits in the Picture panel. A run that
 * fails leaves the bank picture ("AI is not available now").
 */
export function useFirstPictureSwap({ runUuid, initialNote, pictures, revision, dispatch }: UseFirstPictureSwapProps) {
  const [note, setNote] = useState<FirstVersionNote>(initialNote);
  const isDone = useRef(false);
  const revisionRef = useRef(revision);
  revisionRef.current = revision;

  useEffect(() => {
    if (runUuid === null || isDone.current) {
      return;
    }
    const run = pictures.filter((picture) => picture.runUuid === runUuid);
    const ready = run.find((picture) => picture.status === 'ready' && picture.url !== null);
    if (ready !== undefined && ready.url !== null) {
      isDone.current = true;
      if (revisionRef.current === 0) {
        dispatch({
          type: 'setBackground',
          background: {
            kind: 'picture',
            source: { kind: 'ai', pictureUuid: ready.uuid },
            url: getFullAPIFileURL(ready.url),
            focusX: 0.5,
            focusY: 0.5,
            zoom: 1,
          },
          palette: ready.palette.length > 0 ? ready.palette : undefined,
        });
      }
      setNote('aiReady');
      return;
    }
    if (run.length > 0 && run.every((picture) => picture.status === 'failed')) {
      isDone.current = true;
      setNote('aiUnavailable');
    }
  }, [dispatch, pictures, runUuid]);

  return { note, dismiss: () => setNote(null) };
}
